import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, Award, GraduationCap, Briefcase, ArrowRight, ShieldCheck } from "lucide-react";
import { BUSINESS, COMPLIANCE_LINE_SHORT } from "@/lib/constants";
import Seo from "@/components/Seo";
import CtaStrip from "@/components/CtaStrip";

const values = [
    { icon: Award, title: "Excellence", text: "Specialist advice backed by formal qualifications and continuous professional development." },
    { icon: GraduationCap, title: "Education", text: "We explain every option so you can make informed decisions about your finance." },
    { icon: Briefcase, title: "Integrity", text: "Transparent, honest broking — your best interest comes before our commission." }
];

const ABOUT_LD = [
    {
        "@context": "https://schema.org",
        "@type": "Person",
        "@id": "https://financewealth.com.au/#gurpreet-singh",
        "name": "Gurpreet Singh",
        "jobTitle": "Mortgage Broker",
        "description": "Australia-wide online mortgage broker and founder of Finance Wealth. Diploma in Finance & Mortgage Broking, BCom (Accounting & Finance). Authorised Credit Representative No. 548489 of Brass Aggregation Pty Ltd (ACL 561247).",
        "worksFor": { "@id": "https://financewealth.com.au/#business" },
        "telephone": "+61493659554",
        "email": "gurpreet@financewealth.com.au"
    },
    {
        "@context": "https://schema.org",
        "@type": "FinancialService",
        "@id": "https://financewealth.com.au/#business",
        "name": "Finance Wealth",
        "url": "https://financewealth.com.au",
        "telephone": "+61493659554",
        "email": "gurpreet@financewealth.com.au",
        "address": {
            "@type": "PostalAddress",
            "streetAddress": "Office 4444, Ground Floor, 470 St Kilda Road",
            "addressLocality": "Melbourne",
            "addressRegion": "VIC",
            "postalCode": "3004",
            "addressCountry": "AU"
        },
        "areaServed": { "@type": "Country", "name": "Australia" },
        "identifier": [
            { "@type": "PropertyValue", "name": "ABN", "value": "72 642 964 341" },
            { "@type": "PropertyValue", "name": "Australian Credit Licence", "value": "561247" },
            { "@type": "PropertyValue", "name": "Credit Representative", "value": "548489" }
        ]
    }
];

export default function AboutPage() {
    return (
        <div data-testid="page-about" className="pt-20">
            <Seo
                title="About Gurpreet Singh — Mortgage Broker Australia | Finance Wealth"
                description="Meet Gurpreet Singh — qualified Australia-wide mortgage broker with a Diploma in Finance & Mortgage Broking and BCom (Accounting & Finance). Free consultation."
                keywords="Gurpreet Singh mortgage broker, mortgage broker Australia, online mortgage broker, Finance Wealth broker"
                jsonLd={ABOUT_LD}
            />
            <section className="bg-[#F3F1EA]">
                <div className="container-prose py-20 md:py-28">
                    <div className="max-w-3xl">
                        <div className="overline">About</div>
                        <h1 className="mt-4 font-serif text-5xl sm:text-6xl tracking-tight leading-[1.02] text-[#1A362B] font-medium">
                            Meet {BUSINESS.broker}.
                        </h1>
                        <div className="divider-thin my-8" />
                        <p className="text-lg text-[#1C1C1C]/75 leading-relaxed font-light">
                            Australia-wide mortgage broker, accountant by training, and a long-time advocate for honest finance advice.
                        </p>
                    </div>
                </div>
            </section>

            <section className="bg-background">
                <div className="container-prose py-24 max-w-4xl">
                    <div>
                        <div className="overline">Who</div>
                        <h2 className="mt-3 font-serif text-4xl text-[#1A362B] tracking-tight font-medium">A broker who actually understands the numbers.</h2>
                        <div className="divider-thin my-6" />
                        <p className="text-[#1C1C1C]/80 leading-relaxed">
                            Gurpreet Singh founded Finance Wealth to give Australian families and business owners the
                            kind of finance advice they deserve — clear, considered, and centred entirely on their
                            outcomes. With a Bachelor of Commerce in Accounting &amp; Finance and a Diploma in Finance
                            &amp; Mortgage Broking, Gurpreet brings a numbers-first approach to every loan he writes.
                        </p>
                        <p className="mt-5 text-[#1C1C1C]/80 leading-relaxed">
                            Working online and over the phone, Gurpreet helps first home buyers, seasoned investors,
                            refinancers, self-employed clients and small business owners right across Australia —
                            across a panel of more than 60 Australian lenders.
                        </p>

                        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="bg-[#F3F1EA] p-6 rounded-sm">
                                <div className="text-xs tracking-widest uppercase text-[#C69C6D] font-semibold">Qualification</div>
                                <div className="mt-2 font-serif text-lg text-[#1A362B]">Diploma in Finance &amp; Mortgage Broking</div>
                            </div>
                            <div className="bg-[#F3F1EA] p-6 rounded-sm">
                                <div className="text-xs tracking-widest uppercase text-[#C69C6D] font-semibold">Degree</div>
                                <div className="mt-2 font-serif text-lg text-[#1A362B]">BCom · Accounting &amp; Finance</div>
                            </div>
                        </div>

                        <div className="mt-14">
                            <div className="overline">What we do</div>
                            <h2 className="mt-3 font-serif text-3xl text-[#1A362B] tracking-tight font-medium">What Finance Wealth does.</h2>
                            <p className="mt-4 text-[#1C1C1C]/80 leading-relaxed">
                                Finance Wealth is a boutique, Australia-wide online mortgage brokerage. We compare home
                                loans, refinancing, investment loans, commercial finance, self-employed lending, car and
                                asset finance and personal loans across more than 60 Australian lenders — then recommend
                                and manage the option that genuinely fits your situation. Explore our{" "}
                                <Link to="/services" className="text-[#1A362B] font-medium underline decoration-[#C69C6D] underline-offset-2 hover:text-[#C69C6D] transition">services</Link>.
                            </p>
                            <p className="mt-4 text-[#1C1C1C]/80 leading-relaxed">
                                What sets Finance Wealth apart isn't just access to lenders. It's the time taken to
                                understand your situation, the discipline to recommend the right loan structure, and the
                                commitment to be there at every annual review — long after settlement day.
                            </p>
                        </div>

                        <div className="mt-14">
                            <div className="overline">The process</div>
                            <h2 className="mt-3 font-serif text-3xl text-[#1A362B] tracking-tight font-medium">How working with us goes.</h2>
                            <p className="mt-4 text-[#1C1C1C]/80 leading-relaxed">
                                Four simple steps: a free consultation to understand your goals, a comparison across our
                                lender panel with the reasoning in writing, a fully managed application, and support
                                through settlement and beyond. See the detail on our{" "}
                                <Link to="/how-it-works" className="text-[#1A362B] font-medium underline decoration-[#C69C6D] underline-offset-2 hover:text-[#C69C6D] transition">how it works</Link> page.
                            </p>
                        </div>

                        <div className="mt-14 bg-[#F3F1EA] p-8 rounded-sm">
                            <div className="flex items-center gap-3">
                                <ShieldCheck className="w-5 h-5 text-[#1A362B]" />
                                <div className="overline">Licence &amp; authorisations</div>
                            </div>
                            <p data-testid="about-licence" className="mt-4 text-sm text-[#1C1C1C]/75 leading-relaxed">
                                {COMPLIANCE_LINE_SHORT}.
                            </p>
                            <p className="mt-3 text-sm text-[#1C1C1C]/75 leading-relaxed">
                                Full details of how we are paid and how we handle your information are in our{" "}
                                <Link to="/credit-guide" className="text-[#1A362B] font-medium underline decoration-[#C69C6D] underline-offset-2 hover:text-[#C69C6D] transition">Credit Guide &amp; Privacy Disclosure</Link>.
                            </p>
                        </div>

                        <div className="mt-10 space-y-3 text-sm">
                            <a href={`tel:${BUSINESS.phoneTel}`} data-testid="about-phone" className="flex items-center gap-3 text-[#1A362B] hover:text-[#C69C6D] transition">
                                <Phone className="w-4 h-4" /> {BUSINESS.phone}
                            </a>
                            <a href={`mailto:${BUSINESS.email}`} data-testid="about-email" className="flex items-center gap-3 text-[#1A362B] hover:text-[#C69C6D] transition">
                                <Mail className="w-4 h-4" /> {BUSINESS.email}
                            </a>
                        </div>

                        <Link to="/contact" data-testid="about-cta" className="btn-primary mt-10">
                            Book a consultation with Gurpreet <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            <section className="bg-[#F3F1EA]">
                <div className="container-prose py-24">
                    <div className="max-w-2xl mb-12">
                        <div className="overline">What we stand for</div>
                        <h2 className="mt-4 font-serif text-4xl text-[#1A362B] tracking-tight font-medium">Our values.</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {values.map((v, i) => (
                            <div key={v.title} data-testid={`value-${i}`} className="bg-white p-8 rounded-sm border border-border">
                                <div className="w-11 h-11 rounded-sm bg-[#1A362B] text-white grid place-items-center">
                                    <v.icon className="w-5 h-5" />
                                </div>
                                <h3 className="mt-6 font-serif text-2xl text-[#1A362B]">{v.title}</h3>
                                <p className="mt-3 text-sm text-[#1C1C1C]/70 leading-relaxed">{v.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <CtaStrip />
        </div>
    );
}
